import { service } from '@ember/service';
import { t } from 'ember-intl';
import { VStack, Section } from 'tio-reimagined/components/layout';
import ClaimsFinancialsCalculationSectionBreakdown from './calculation-section-breakdown.gts';
import Component from '@glimmer/component';
import type { TasClaimsFinancials } from '../../../../types/tuition-assistance.ts';
import type Intl from 'ember-intl/services/intl';

export interface S {
  Element: HTMLDivElement;
  Args: {
    claimsFinancialsFields: Readonly<TasClaimsFinancials>;
    showDetails: boolean;
  };
  Blocks: {
    default: [];
  };
}

export type CalculationInputs = {
  dataKey: keyof TasClaimsFinancials;
  label: string;
  type: string;
};

export default class TuitionAssistanceProgramDetailsCalculationsReadonlyCard extends Component<S> {
  @service declare intl: Intl;

  get itemizedBillCalculations(): CalculationInputs[] {
    return [
      {
        dataKey: 'CF_TUITION_CHARGED',
        label: this.intl.t('common.tuition_assistance.claims_financials.total_tuition_charged'),
        type: 'currency',
      },
      {
        dataKey: 'CF_DISCOUNT_AMOUNT',
        label: this.intl.t('common.tuition_assistance.claims_financials.discount_amount'),
        type: 'currency',
      },
      {
        dataKey: 'CF_TUITION_AFTER_DISCOUNT',
        label: this.intl.t('common.tuition_assistance.claims_financials.total_after_discount'),
        type: 'currency',
      },
      {
        dataKey: 'CF_FEES',
        label: this.intl.t('common.tuition_assistance.claims_financials.fees'),
        type: 'currency',
      },
      {
        dataKey: 'CF_INVOICE_TOTAL',
        label: this.intl.t('common.tuition_assistance.claims_financials.invoice_total'),
        type: 'currency',
      },
    ];
  }

  get proofOfPaymentCalculations(): CalculationInputs[] {
    return [
      {
        dataKey: 'CF_EMPLOYEE_POP_AMOUNT',
        label: this.intl.t('common.tuition_assistance.claims_financials.employee_pop_amount'),
        type: 'currency',
      },
      {
        dataKey: 'CF_LOAN_POP_AMOUNT',
        label: this.intl.t('common.tuition_assistance.claims_financials.loan_amount'),
        type: 'currency',
      },
      {
        dataKey: 'CF_GRANT_POP_AMOUNT',
        label: this.intl.t('common.tuition_assistance.claims_financials.grant_scholarship_amount'),
        type: 'currency',
      },
      {
        dataKey: 'CF_OTHER_POP_AMOUNT',
        label: this.intl.t('common.tuition_assistance.claims_financials.other_pop_amount'),
        type: 'currency',
      },
      {
        dataKey: 'CF_TOTAL_POP_AMOUNT',
        label: this.intl.t('common.tuition_assistance.claims_financials.total_pop_amount'),
        type: 'currency',
      },
      {
        dataKey: 'CF_UNPAID_BALANCE',
        label: this.intl.t('common.tuition_assistance.claims_financials.unpaid_balance'),
        type: 'currency',
      },
    ];
  }

  get approvalCalculations(): CalculationInputs[] {
    return [
      {
        dataKey: 'CF_APPLICABLE_GRANT',
        label: this.intl.t('common.tuition_assistance.claims_financials.applicable_grant'),
        type: 'currency',
      },
      {
        dataKey: 'CF_GRADED_TUITION',
        label: this.intl.t('common.tuition_assistance.claims_financials.graded_tuition_amount'),
        type: 'currency',
      },
      {
        dataKey: 'CF_GRANT_DISTRIBUTION',
        label: this.intl.t('common.tuition_assistance.claims_financials.grant_distribution'),
        type: 'currency',
      },
      {
        dataKey: 'CF_PERCENT_TOTAL_COST',
        label: this.intl.t('common.tuition_assistance.claims_financials.percent_total_cost'),
        type: 'percent',
      },
      {
        dataKey: 'CF_BENEFIT_FRACTIONAL_PERCENT_APPLIED',
        label: this.intl.t('common.tuition_assistance.claims_financials.benefit_percent'),
        type: 'percent',
      },
      {
        dataKey: 'CF_BENEFIT_PERCENT_OF_GRADED_TUITION',
        label: this.intl.t(
          'common.tuition_assistance.claims_financials.benefit_percent_x_graded_tuition'
        ),
        type: 'currency',
      },
      {
        dataKey: 'CF_GRADED_TUITION_LESS_GRANT_DISTRIBUTION',
        label: this.intl.t(
          'common.tuition_assistance.claims_financials.graded_tuition_amount_minus_grant_distribution'
        ),
        type: 'currency',
      },
      {
        dataKey: 'CF_REIMBURSEMENT_TOTAL',
        label: this.intl.t('common.tuition_assistance.claims_financials.approved_total'),
        type: 'currency',
      },
    ];
  }

  <template>
    {{#if @showDetails}}
      <VStack>
        <Section>
          <:header>
            {{t "common.tuition_assistance.claims_financials.default"}}
          </:header>
          <:body>
            <ClaimsFinancialsCalculationSectionBreakdown
              @claimsFinancialsFields={{@claimsFinancialsFields}}
              @lineItems={{this.itemizedBillCalculations}}
              @sectionHeading={{t "common.tuition_assistance.claims_financials.itemized_bill"}}
            />
            <ClaimsFinancialsCalculationSectionBreakdown
              @claimsFinancialsFields={{@claimsFinancialsFields}}
              @lineItems={{this.proofOfPaymentCalculations}}
              @sectionHeading={{t "common.tuition_assistance.claims_financials.proof_of_payment"}}
            />
            <ClaimsFinancialsCalculationSectionBreakdown
              @claimsFinancialsFields={{@claimsFinancialsFields}}
              @lineItems={{this.approvalCalculations}}
              @sectionHeading={{t
                "common.tuition_assistance.claims_financials.approval_calculations"
              }}
            />
          </:body>
        </Section>
      </VStack>
    {{/if}}
  </template>
}
